import {
  Link,
  Outlet,
  createRootRouteWithContext,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import type { SessionUser } from "@contractorhub/shared";
import { FileQuestion, RefreshCw, ServerCrash } from "lucide-react";
import { Button, Card, EmptyState, buttonClass } from "../components/ui";
import { errorMessage } from "../lib/api";

export type RouterContext = {
  queryClient: QueryClient;
  /** Resolved once in main.tsx and refreshed by the auth guard. */
  user: SessionUser | null;
};

/**
 * The error boundary.
 *
 * There was none — `errorComponent` appeared nowhere in the app — so any loader
 * failure fell through to TanStack Router's stock component: unstyled monospace
 * error text on a white page, no shell, no way back, and no retry.
 *
 * That is not an edge case here. The database auto-pauses when idle and *rejects*
 * the connection that wakes it; `withDbRetry` absorbs that for reads, but a cold
 * start, a gateway timeout, or a dropped connection still surfaces as a thrown
 * loader. The single most likely thing a user wants is to try again, so that is
 * the primary button.
 */
function RouteError({ error, reset }: ErrorComponentProps) {
  return (
    <div className="grid min-h-screen place-items-center p-6">
      <Card className="w-full max-w-md">
        <EmptyState
          icon={ServerCrash}
          title="Something went wrong"
          description={errorMessage(error)}
          action={
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button icon={RefreshCw} onClick={reset}>
                Try again
              </Button>
              <Link to="/" className={buttonClass({ variant: "secondary" })}>
                Go home
              </Link>
            </div>
          }
        />
      </Card>
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  errorComponent: RouteError,
  notFoundComponent: () => (
    <div className="grid min-h-screen place-items-center p-6">
      <Card className="w-full max-w-md">
        <EmptyState
          icon={FileQuestion}
          title="Page not found"
          description="The page you were looking for doesn't exist."
          action={
            <Link to="/" className={buttonClass({ variant: "secondary" })}>
              Go home
            </Link>
          }
        />
      </Card>
    </div>
  ),
});
